/**
 * Small async helpers shared by the dashboard data hooks.
 *
 * Extracted from `useAnalyticsSummary` (#152) so `useDashboardStats` can use
 * the same fan-out discipline without a second copy drifting from the first.
 */

/**
 * Runs `fn` over `items` with at most `limit` in flight, preserving order.
 *
 * The backend rate-limits every client to 100 requests/minute globally
 * (`backend/src/server.ts`), and the dashboard hooks issue one request per
 * event on top of whatever else the SPA is doing. A pool keeps the fan-out from
 * arriving as one burst. It does NOT make the total request count smaller, so
 * it cannot on its own stop a large organizer from being throttled — which is
 * why callers must count every throttled item as a disclosed gap rather than
 * dropping it.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let cursor = 0

  const worker = async () => {
    for (;;) {
      const index = cursor++
      if (index >= items.length) {
        return
      }
      results[index] = await fn(items[index], index)
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}
